import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatInt, formatLatency, formatPercent, ratio } from "@/lib/statsFormat";
import type { CallStatsTotals } from "@/services/callStatsService";

interface ConversationQualityProps {
  totals: CallStatsTotals;
}

const ConversationQuality = ({ totals }: ConversationQualityProps) => {
  const turns = totals.turns ?? 0;
  const callsWithTurns = totals.calls_with_turns ?? 0;

  const bars = [
    { label: "LLM first token", value: totals.llm_ttft_avg_ms, color: "var(--chart-1)" },
    { label: "TTS first audio", value: totals.tts_ttfb_avg_ms, color: "var(--chart-2)" },
    { label: "STT end of speech", value: totals.stt_eou_avg_ms, color: "var(--chart-4)" },
  ];
  const max = Math.max(...bars.map((bar) => bar.value ?? 0));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Conversation quality</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-3">
          <h3 className="text-sm font-semibold">Response time per turn</h3>
          {bars.map((bar) => (
            <div key={bar.label} className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span>{bar.label}</span>
                <span className="tabular-nums text-muted-foreground">
                  {formatLatency(bar.value)}
                </span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-muted">
                {bar.value != null && max > 0 && (
                  <div
                    className="h-full rounded-full"
                    style={{ width: `${(bar.value / max) * 100}%`, background: bar.color }}
                  />
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-8">
          <div>
            <p className="text-xl font-bold">
              {formatPercent(ratio(totals.interrupted_turns ?? 0, turns))}
            </p>
            <p className="text-xs text-muted-foreground">Turns interrupted</p>
          </div>
          <div>
            <p className="text-xl font-bold">
              {callsWithTurns > 0 ? (turns / callsWithTurns).toFixed(1) : "—"}
            </p>
            <p className="text-xs text-muted-foreground">Turns per call</p>
          </div>
        </div>

        <p className="text-xs text-muted-foreground">
          from {formatInt(callsWithTurns)} calls with per-turn data
        </p>
      </CardContent>
    </Card>
  );
};

export default ConversationQuality;
